import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Folder,
  FolderOpen,
  InsertDriveFile,
  ExpandMore,
  ExpandLess,
  Terminal as TerminalIcon,
  Preview,
  Code,
  Refresh,
  Settings,
  Download,
  Upload
} from '@mui/icons-material';
import MonacoEditor from '@monaco-editor/react';
import { webContainerService } from '../../services/webContainerService';
import { webContainerIntegration } from '../../services/webContainerIntegration';
import { claudeCodeService } from '../../services/claudeCodeService';
import TerminalComponent from './TerminalComponent';

const BoltStyleCodeDevelopment = ({ 
  projectId,
  initialTasks = [],
  onTaskComplete,
  onCodeChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Core states
  const [webContainer, setWebContainer] = useState(null);
  const [tasks, setTasks] = useState(initialTasks);
  const [currentTask, setCurrentTask] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // File system states
  const [files, setFiles] = useState(new Map());
  const [fileTree, setFileTree] = useState({});
  const [openFiles, setOpenFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['src', 'public']));
  
  // Real-time file operation states
  const [creatingFiles, setCreatingFiles] = useState(new Set());
  const [updatingFiles, setUpdatingFiles] = useState(new Set());
  const [recentlyCreated, setRecentlyCreated] = useState(new Set());
  
  // Interface states
  const [activeTab, setActiveTab] = useState('editor'); // 'editor', 'preview'
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Refs
  const editorRef = useRef(null);
  const previewRef = useRef(null);
  
  // Initialize WebContainer with integration
  useEffect(() => {
    const initWebContainer = async () => {
      try {
        console.log('🚀 Initializing WebContainer for project:', projectId);
        const container = await webContainerIntegration.initialize(projectId);
        setWebContainer(container);
        
        // Set up event listeners for WebContainer events
        setupWebContainerEventListeners();
        
        // Create initial project structure immediately
        console.log('📁 Creating initial project structure...');
        await webContainerIntegration.createProject('express');
        
        // Force update file tree after creation
        console.log('🔄 Updating file tree...');
        await updateFileTree(container);
        
        // Sync with backend workspace to get existing files
        console.log('🔄 Syncing with backend workspace...');
        await webContainerIntegration.syncWithBackend();
        
        console.log('✅ WebContainer initialization complete');
        
      } catch (error) {
        console.error('❌ Failed to initialize WebContainer:', error);
        // Create fallback file structure
        await createFallbackFileStructure();
      }
    };
    
    initWebContainer();
    
    return () => {
      // Cleanup event listeners
      cleanupWebContainerEventListeners();
    };
  }, [projectId]);
  
  // Create fallback file structure if WebContainer fails
  const createFallbackFileStructure = async () => {
    console.log('📁 Creating fallback file structure...');
    const fallbackTree = {
      'package.json': { type: 'file', path: 'package.json' },
      'server.js': { type: 'file', path: 'server.js' },
      'README.md': { type: 'file', path: 'README.md' },
      'src': {
        type: 'folder',
        children: {
          'routes': {
            type: 'folder', 
            children: {
              'index.js': { type: 'file', path: 'src/routes/index.js' }
            }
          },
          'middleware': {
            type: 'folder',
            children: {
              'auth.js': { type: 'file', path: 'src/middleware/auth.js' }
            }
          }
        }
      },
      'public': {
        type: 'folder',
        children: {
          'index.html': { type: 'file', path: 'public/index.html' },
          'style.css': { type: 'file', path: 'public/style.css' }
        }
      }
    };
    
    setFileTree(fallbackTree);
    
    // Create files map
    const filesMap = new Map();
    filesMap.set('package.json', { 
      name: 'package.json', 
      path: 'package.json', 
      content: '{\n  "name": "project",\n  "version": "1.0.0"\n}' 
    });
    filesMap.set('server.js', { 
      name: 'server.js', 
      path: 'server.js', 
      content: 'console.log("Hello World");' 
    });
    setFiles(filesMap);
  };
  
  // Set up WebContainer event listeners
  const setupWebContainerEventListeners = () => {
    window.addEventListener('webcontainer:fileCreated', handleWebContainerFileCreated);
    window.addEventListener('webcontainer:fileUpdated', handleWebContainerFileUpdated);
    window.addEventListener('webcontainer:syncComplete', handleWebContainerSyncComplete);
    window.addEventListener('webcontainer:installProgress', handleInstallProgress);
    window.addEventListener('webcontainer:installComplete', handleInstallComplete);
    window.addEventListener('webcontainer:serverStarted', handleServerStarted);
  };
  
  const cleanupWebContainerEventListeners = () => {
    window.removeEventListener('webcontainer:fileCreated', handleWebContainerFileCreated);
    window.removeEventListener('webcontainer:fileUpdated', handleWebContainerFileUpdated);
    window.removeEventListener('webcontainer:syncComplete', handleWebContainerSyncComplete);
    window.removeEventListener('webcontainer:installProgress', handleInstallProgress);
    window.removeEventListener('webcontainer:installComplete', handleInstallComplete);
    window.removeEventListener('webcontainer:serverStarted', handleServerStarted);
  };
  
  const handleWebContainerFileCreated = (event) => {
    const { filePath, content } = event.detail;
    console.log('📁 File created in WebContainer:', filePath);
    
    // Add to files map immediately
    setFiles(prev => {
      const newMap = new Map(prev);
      newMap.set(filePath, {
        name: filePath.split('/').pop(),
        path: filePath,
        content,
        size: content.length
      });
      return newMap;
    });
    
    // Update file tree
    updateFileTree(webContainer);
    
    // Show streaming effect
    showFileCreateAnimation(filePath);
  };
  
  const handleWebContainerFileUpdated = (event) => {
    const { filePath, content } = event.detail;
    console.log('📝 File updated in WebContainer:', filePath);
    
    // Update files map
    setFiles(prev => {
      const newMap = new Map(prev);
      const existingFile = newMap.get(filePath);
      if (existingFile) {
        newMap.set(filePath, { ...existingFile, content, size: content.length });
      }
      return newMap;
    });
    
    // Update current file if it's open
    if (activeFile?.path === filePath) {
      setActiveFile(prev => ({ ...prev, content }));
    }
    
    // Show streaming effect
    showFileUpdateAnimation(filePath);
  };
  
  const showFileCreateAnimation = (filePath) => {
    // Add to creating files state with animation
    setCreatingFiles(prev => new Set([...prev, filePath]));
    
    // Remove after animation completes
    setTimeout(() => {
      setCreatingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(filePath);
        return newSet;
      });
      
      // Add to recently created for highlight effect
      setRecentlyCreated(prev => new Set([...prev, filePath]));
      
      // Remove from recently created after 3 seconds
      setTimeout(() => {
        setRecentlyCreated(prev => {
          const newSet = new Set(prev);
          newSet.delete(filePath);
          return newSet;
        });
      }, 3000);
    }, 1500);
  };
  
  const showFileUpdateAnimation = (filePath) => {
    // Add to updating files state with animation
    setUpdatingFiles(prev => new Set([...prev, filePath]));
    
    // Remove after animation completes
    setTimeout(() => {
      setUpdatingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(filePath);
        return newSet;
      });
    }, 1000);
  };
  
  const handleInstallProgress = (event) => {
    const { output } = event.detail;
    console.log('📦 Install progress:', output);
    setTasks(prev => prev.map(task => 
      task.id === 'task-2' 
        ? { ...task, status: 'running', currentStep: 'Installing packages...' }
        : task
    ));
  };
  
  const handleInstallComplete = () => {
    console.log('✅ Dependencies installed');
    setTasks(prev => prev.map(task => 
      task.id === 'task-2' 
        ? { ...task, status: 'completed', currentStep: 'Dependencies installed' }
        : task
    ));
  };
  
  const handleServerStarted = (event) => {
    const { url } = event.detail;
    console.log('🚀 Server started at:', url);
    setPreviewUrl(url);
    setTasks(prev => prev.map(task => 
      task.id === 'task-5' 
        ? { ...task, status: 'completed', currentStep: 'Server running' }
        : task
    ));
  };

  const handleWebContainerSyncComplete = (event) => {
    const { workspaceData } = event.detail;
    console.log('🔄 WebContainer sync complete:', workspaceData);
    
    if (workspaceData?.directory_tree) {
      // Convert backend directory tree to frontend format
      const convertTreeToFrontendFormat = (tree) => {
        const result = {};
        for (const [name, item] of Object.entries(tree)) {
          if (item.type === 'directory') {
            result[name] = {
              type: 'folder',
              children: convertTreeToFrontendFormat(item.children)
            };
          } else if (item.type === 'file') {
            result[name] = {
              type: 'file',
              path: item.path,
              size: item.size,
              extension: item.extension
            };
          }
        }
        return result;
      };
      
      const frontendTree = convertTreeToFrontendFormat(workspaceData.directory_tree);
      setFileTree(frontendTree);
      console.log('📂 Updated file tree from backend sync:', frontendTree);
    }
    
    if (workspaceData?.files) {
      // Update files map with synced files
      const filesMap = new Map();
      workspaceData.files.forEach(file => {
        filesMap.set(file.path, {
          name: file.filename,
          path: file.path,
          content: '', // Content will be loaded on demand
          size: file.size,
          directory: file.directory
        });
      });
      setFiles(filesMap);
      console.log('📄 Updated files map from backend sync:', filesMap.size, 'files');
    }
  };
  
  // Update file tree from container
  const updateFileTree = useCallback(async (container) => {
    if (!container) return;
    
    try {
      const tree = await buildFileTree(container, '');
      setFileTree(tree);
      
      // Update files map
      const filesMap = new Map();
      await traverseFileTree(container, '', filesMap);
      setFiles(filesMap);
    } catch (error) {
      console.error('Failed to update file tree:', error);
    }
  }, []);
  
  // Build file tree structure
  const buildFileTree = async (container, path) => {
    const entries = await container.fs.readdir(path, { withFileTypes: true });
    const tree = {};
    
    for (const entry of entries) {
      const fullPath = path ? `${path}/${entry.name}` : entry.name;
      
      if (entry.isDirectory()) {
        tree[entry.name] = {
          type: 'folder',
          children: await buildFileTree(container, fullPath)
        };
      } else {
        tree[entry.name] = {
          type: 'file',
          path: fullPath
        };
      }
    }
    
    return tree;
  };
  
  // Traverse file tree to build files map
  const traverseFileTree = async (container, path, filesMap) => {
    const entries = await container.fs.readdir(path, { withFileTypes: true });
    
    for (const entry of entries) {
      const fullPath = path ? `${path}/${entry.name}` : entry.name;
      
      if (entry.isDirectory()) {
        await traverseFileTree(container, fullPath, filesMap);
      } else {
        try {
          const content = await container.fs.readFile(fullPath, 'utf-8');
          filesMap.set(fullPath, {
            name: entry.name,
            path: fullPath,
            content,
            size: content.length
          });
        } catch (error) {
          console.error(`Failed to read file ${fullPath}:`, error);
        }
      }
    }
  };
  
  // File operations
  const openFile = useCallback(async (filePath) => {
    if (!webContainer) return;
    
    try {
      const content = await webContainer.fs.readFile(filePath, 'utf-8');
      const file = {
        name: filePath.split('/').pop(),
        path: filePath,
        content
      };
      
      // Add to open files if not already open
      setOpenFiles(prev => {
        const exists = prev.find(f => f.path === filePath);
        if (!exists) {
          return [...prev, file];
        }
        return prev;
      });
      
      setActiveFile(file);
    } catch (error) {
      console.error('Failed to open file:', error);
    }
  }, [webContainer]);
  
  const saveFile = useCallback(async (filePath, content) => {
    if (!webContainer) return;
    
    try {
      await webContainer.fs.writeFile(filePath, content);
      
      // Update files map
      setFiles(prev => {
        const newMap = new Map(prev);
        const file = newMap.get(filePath);
        if (file) {
          newMap.set(filePath, { ...file, content });
        }
        return newMap;
      });
      
      // Update open files
      setOpenFiles(prev => 
        prev.map(f => f.path === filePath ? { ...f, content } : f)
      );
      
      if (onCodeChange) {
        onCodeChange(filePath, content);
      }
    } catch (error) {
      console.error('Failed to save file:', error);
    }
  }, [webContainer, onCodeChange]);
  
  const closeFile = useCallback((filePath) => {
    setOpenFiles(prev => prev.filter(f => f.path !== filePath));
    if (activeFile?.path === filePath) {
      const remaining = openFiles.filter(f => f.path !== filePath);
      setActiveFile(remaining.length > 0 ? remaining[remaining.length - 1] : null);
    }
  }, [activeFile, openFiles]);
  
  
  // Task execution with real WebContainer operations
  const executeTask = useCallback(async (task) => {
    if (!webContainer) return;
    
    setCurrentTask(task);
    setIsGenerating(true);
    setProgress(0);
    
    try {
      // Update task status to running
      setTasks(prev => prev.map(t => 
        t.id === task.id 
          ? { ...t, status: 'running', currentStep: 'Starting...' }
          : t
      ));

      switch (task.id) {
        case 'task-1': // Import Project
          await executeImportProject(task);
          break;
        case 'task-2': // Install Dependencies
          await executeInstallDependencies(task);
          break;
        case 'task-3': // Generate Code
          await executeGenerateCode(task);
          break;
        case 'task-5': // Start Application
          await executeStartApplication(task);
          break;
        default:
          await executeDefaultTask(task);
      }
      
      setProgress(100);
      setTasks(prev => prev.map(t => 
        t.id === task.id 
          ? { ...t, status: 'completed', currentStep: 'Completed' }
          : t
      ));
      
      if (onTaskComplete) {
        onTaskComplete(task);
      }
      
    } catch (error) {
      console.error('Task execution failed:', error);
      setTasks(prev => prev.map(t => 
        t.id === task.id 
          ? { ...t, status: 'error', currentStep: 'Failed: ' + error.message }
          : t
      ));
    } finally {
      setIsGenerating(false);
      setCurrentTask(null);
    }
  }, [webContainer, onTaskComplete]);

  const executeImportProject = async (task) => {
    setProgress(20);
    setTasks(prev => prev.map(t => 
      t.id === task.id 
        ? { ...t, currentStep: 'Creating project structure...' }
        : t
    ));
    
    try {
      // Try WebContainer first
      await webContainerIntegration.createProject('express');
      setProgress(60);
      
      // Force update file tree
      await updateFileTree(webContainer);
      setProgress(80);
      
    } catch (error) {
      console.error('WebContainer project creation failed, using fallback:', error);
      // Create files manually in frontend state
      await createImmediateFileStructure();
    }
    
    setProgress(100);
  };
  
  // Create immediate file structure in frontend state
  const createImmediateFileStructure = async () => {
    console.log('📁 Creating immediate file structure...');
    
    // Create detailed file tree
    const projectFiles = {
      'package.json': {
        type: 'file',
        path: 'package.json'
      },
      'server.js': {
        type: 'file', 
        path: 'server.js'
      },
      'README.md': {
        type: 'file',
        path: 'README.md'
      },
      '.env': {
        type: 'file',
        path: '.env'
      },
      'src': {
        type: 'folder',
        children: {
          'routes': {
            type: 'folder',
            children: {
              'index.js': { type: 'file', path: 'src/routes/index.js' },
              'api.js': { type: 'file', path: 'src/routes/api.js' },
              'users.js': { type: 'file', path: 'src/routes/users.js' }
            }
          },
          'middleware': {
            type: 'folder',
            children: {
              'auth.js': { type: 'file', path: 'src/middleware/auth.js' },
              'cors.js': { type: 'file', path: 'src/middleware/cors.js' },
              'logger.js': { type: 'file', path: 'src/middleware/logger.js' }
            }
          },
          'models': {
            type: 'folder',
            children: {
              'User.js': { type: 'file', path: 'src/models/User.js' },
              'Product.js': { type: 'file', path: 'src/models/Product.js' }
            }
          },
          'controllers': {
            type: 'folder',
            children: {
              'userController.js': { type: 'file', path: 'src/controllers/userController.js' },
              'productController.js': { type: 'file', path: 'src/controllers/productController.js' }
            }
          }
        }
      },
      'public': {
        type: 'folder',
        children: {
          'index.html': { type: 'file', path: 'public/index.html' },
          'style.css': { type: 'file', path: 'public/style.css' },
          'app.js': { type: 'file', path: 'public/app.js' },
          'assets': {
            type: 'folder',
            children: {
              'images': {
                type: 'folder',
                children: {
                  'logo.png': { type: 'file', path: 'public/assets/images/logo.png' }
                }
              }
            }
          }
        }
      },
      'tests': {
        type: 'folder',
        children: {
          'user.test.js': { type: 'file', path: 'tests/user.test.js' },
          'api.test.js': { type: 'file', path: 'tests/api.test.js' }
        }
      }
    };
    
    setFileTree(projectFiles);
    
    // Create files with content
    const filesWithContent = new Map();
    
    // Add all files with sample content
    const fileContents = {
      'package.json': JSON.stringify({
        "name": `ai-project-${projectId}`,
        "version": "1.0.0",
        "description": "AI-generated Express.js application",
        "main": "server.js",
        "scripts": {
          "start": "node server.js",
          "dev": "nodemon server.js",
          "test": "jest"
        },
        "dependencies": {
          "express": "^4.18.2",
          "cors": "^2.8.5",
          "dotenv": "^16.0.3",
          "morgan": "^1.10.0"
        },
        "devDependencies": {
          "nodemon": "^2.0.22",
          "jest": "^29.5.0"
        }
      }, null, 2),
      
      'server.js': `const express = require('express');
const cors = require('cors');
const morgan = require('morgan');
require('dotenv').config();

const app = express();
const PORT = process.env.PORT || 3000;

// Middleware
app.use(cors());
app.use(morgan('combined'));
app.use(express.json());
app.use(express.static('public'));

// Routes
const userRoutes = require('./src/routes/users');
const apiRoutes = require('./src/routes/api');

app.use('/api/users', userRoutes);
app.use('/api', apiRoutes);

// Health check
app.get('/health', (req, res) => {
  res.json({ 
    status: 'healthy', 
    timestamp: new Date().toISOString(),
    uptime: process.uptime()
  });
});

// Root route
app.get('/', (req, res) => {
  res.json({ 
    message: 'Welcome to AI-Generated Express.js API!',
    version: '1.0.0',
    endpoints: {
      health: '/health',
      users: '/api/users',
      api: '/api'
    }
  });
});

// Start server
app.listen(PORT, () => {
  console.log(\`🚀 Server running on http://localhost:\${PORT}\`);
  console.log(\`📚 API Documentation available at http://localhost:\${PORT}/api\`);
});`,

      'README.md': `# 🚀 AI-Generated Express.js Application

This is a full-featured Express.js application generated by AI with real-time development capabilities.

## 🌟 Features

- **Express.js** - Fast, minimalist web framework
- **CORS** - Cross-origin resource sharing
- **Morgan** - HTTP request logger
- **dotenv** - Environment variable management
- **RESTful APIs** - User and product management
- **Real-time Development** - Live reload and hot updates

## 📁 Project Structure

\`\`\`
├── src/
│   ├── routes/          # API route definitions
│   ├── middleware/      # Custom middleware
│   ├── models/          # Data models
│   └── controllers/     # Business logic
├── public/              # Static assets
├── tests/               # Test files
├── server.js            # Main application file
└── package.json         # Dependencies and scripts
\`\`\`

## 🚀 Getting Started

1. **Install dependencies:**
   \`\`\`bash
   npm install
   \`\`\`

2. **Start development server:**
   \`\`\`bash
   npm run dev
   \`\`\`

3. **Run tests:**
   \`\`\`bash
   npm test
   \`\`\`

## 🔌 API Endpoints

- \`GET /\` - Welcome message
- \`GET /health\` - Health check
- \`GET /api/users\` - Get all users
- \`POST /api/users\` - Create new user
- \`GET /api/users/:id\` - Get user by ID

## 🛠️ Development

This project supports real-time development with:
- Hot reload
- Live file updates
- WebContainer integration
- In-browser terminal

## 📝 License

Generated by AI - Free to use and modify!`,

      'src/routes/api.js': `const express = require('express');
const router = express.Router();

// API info
router.get('/', (req, res) => {
  res.json({
    name: 'AI-Generated API',
    version: '1.0.0',
    description: 'RESTful API built with Express.js',
    endpoints: {
      users: '/api/users',
      health: '/health'
    },
    documentation: 'https://api-docs.example.com'
  });
});

// API stats
router.get('/stats', (req, res) => {
  res.json({
    totalRequests: Math.floor(Math.random() * 10000),
    uptime: process.uptime(),
    timestamp: new Date().toISOString(),
    memory: process.memoryUsage()
  });
});

module.exports = router;`,

      'src/routes/users.js': `const express = require('express');
const router = express.Router();

// Mock user data
let users = [
  { id: 1, name: 'John Doe', email: 'john@example.com', role: 'admin' },
  { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'user' },
  { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'user' }
];

// Get all users
router.get('/', (req, res) => {
  res.json({
    success: true,
    count: users.length,
    data: users
  });
});

// Get user by ID
router.get('/:id', (req, res) => {
  const id = parseInt(req.params.id);
  const user = users.find(u => u.id === id);
  
  if (!user) {
    return res.status(404).json({
      success: false,
      message: 'User not found'
    });
  }
  
  res.json({
    success: true,
    data: user
  });
});

// Create new user
router.post('/', (req, res) => {
  const { name, email, role = 'user' } = req.body;
  
  if (!name || !email) {
    return res.status(400).json({
      success: false,
      message: 'Name and email are required'
    });
  }
  
  const newUser = {
    id: users.length + 1,
    name,
    email,
    role,
    createdAt: new Date().toISOString()
  };
  
  users.push(newUser);
  
  res.status(201).json({
    success: true,
    message: 'User created successfully',
    data: newUser
  });
});

// Update user
router.put('/:id', (req, res) => {
  const id = parseInt(req.params.id);
  const userIndex = users.findIndex(u => u.id === id);
  
  if (userIndex === -1) {
    return res.status(404).json({
      success: false,
      message: 'User not found'
    });
  }
  
  users[userIndex] = { ...users[userIndex], ...req.body };
  
  res.json({
    success: true,
    message: 'User updated successfully',
    data: users[userIndex]
  });
});

// Delete user
router.delete('/:id', (req, res) => {
  const id = parseInt(req.params.id);
  const userIndex = users.findIndex(u => u.id === id);
  
  if (userIndex === -1) {
    return res.status(404).json({
      success: false,
      message: 'User not found'
    });
  }
  
  users.splice(userIndex, 1);
  
  res.json({
    success: true,
    message: 'User deleted successfully'
  });
});

module.exports = router;`,

      'public/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>🚀 AI-Generated App</title>
    <link rel="stylesheet" href="style.css">
</head>
<body>
    <div class="container">
        <header class="header">
            <h1>🚀 AI-Generated Express.js Application</h1>
            <p class="subtitle">Built with WebContainer technology</p>
        </header>
        
        <main class="main">
            <div class="status-card">
                <h2>✅ Application Status</h2>
                <div class="status-grid">
                    <div class="status-item">
                        <span class="label">Server:</span>
                        <span class="value">Running</span>
                    </div>
                    <div class="status-item">
                        <span class="label">Framework:</span>
                        <span class="value">Express.js</span>
                    </div>
                    <div class="status-item">
                        <span class="label">Environment:</span>
                        <span class="value">Development</span>
                    </div>
                </div>
            </div>

            <div class="api-card">
                <h2>🔌 API Endpoints</h2>
                <div class="endpoints">
                    <div class="endpoint">
                        <span class="method get">GET</span>
                        <span class="path">/api/users</span>
                        <span class="description">Get all users</span>
                    </div>
                    <div class="endpoint">
                        <span class="method post">POST</span>
                        <span class="path">/api/users</span>
                        <span class="description">Create new user</span>
                    </div>
                    <div class="endpoint">
                        <span class="method get">GET</span>
                        <span class="path">/health</span>
                        <span class="description">Health check</span>
                    </div>
                </div>
            </div>

            <div class="users-card">
                <h2>👥 Users</h2>
                <div id="users-container">
                    <div class="loading">Loading users...</div>
                </div>
            </div>
        </main>
    </div>

    <script src="app.js"></script>
</body>
</html>`
    };
    
    // Add files to the map with streaming animations
    for (const [filePath, content] of Object.entries(fileContents)) {
      filesWithContent.set(filePath, {
        name: filePath.split('/').pop(),
        path: filePath,
        content,
        size: content.length
      });
      
      // Show streaming animation for each file
      showFileCreateAnimation(filePath);
      
      // Add small delay between files for visual effect
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    setFiles(filesWithContent);
    console.log('✅ Immediate file structure created with', filesWithContent.size, 'files');
  };

  const executeInstallDependencies = async (task) => {
    setProgress(10);
    setTasks(prev => prev.map(t => 
      t.id === task.id 
        ? { ...t, currentStep: 'Installing npm packages...' }
        : t
    ));
    
    await webContainerIntegration.installDependencies();
    
    setProgress(100);
  };

  const executeGenerateCode = async (task) => {
    setProgress(10);
    setTasks(prev => prev.map(t => 
      t.id === task.id 
        ? { ...t, currentStep: 'Connecting to AI code generator...' }
        : t
    ));

    try {
      // Start code generation via backend
      await claudeCodeService.generateCodeFromTask(projectId, {
        task_description: 'Generate a complete web application based on project requirements',
        task_type: 'implementation',
        language: 'javascript',
        framework: 'express',
        stream_enabled: true
      });
      
      setProgress(90);
      setTasks(prev => prev.map(t => 
        t.id === task.id 
          ? { ...t, currentStep: 'Code generation complete' }
          : t
      ));
      
    } catch (error) {
      console.error('Code generation failed:', error);
      // Create fallback files
      await createFallbackFiles();
    }
    
    setProgress(100);
  };

  const createFallbackFiles = async () => {
    // Create some example files if backend generation fails
    const exampleFiles = {
      'src/routes/api.js': `const express = require('express');
const router = express.Router();

// API Routes
router.get('/users', (req, res) => {
  res.json([
    { id: 1, name: 'John Doe', email: 'john@example.com' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com' }
  ]);
});

router.post('/users', (req, res) => {
  const { name, email } = req.body;
  // In a real app, save to database
  const newUser = { id: Date.now(), name, email };
  res.status(201).json(newUser);
});

module.exports = router;`,

      'src/middleware/auth.js': `const authMiddleware = (req, res, next) => {
  const token = req.headers.authorization;
  
  if (!token) {
    return res.status(401).json({ error: 'No token provided' });
  }
  
  // In a real app, verify JWT token
  // For demo purposes, accept any token
  req.user = { id: 1, name: 'Demo User' };
  next();
};

module.exports = authMiddleware;`,

      'public/app.js': `// Frontend JavaScript
document.addEventListener('DOMContentLoaded', function() {
    // Fetch users from API
    fetch('/api/users')
        .then(response => response.json())
        .then(users => {
            const usersList = document.getElementById('users-list');
            if (usersList) {
                usersList.innerHTML = users.map(user => 
                    \`<li>\${user.name} - \${user.email}</li>\`
                ).join('');
            }
        })
        .catch(error => console.error('Error:', error));
});`
    };

    for (const [filePath, content] of Object.entries(exampleFiles)) {
      await webContainerIntegration.createFile(filePath, content);
    }
  };


  const executeStartApplication = async (task) => {
    setProgress(20);
    setTasks(prev => prev.map(t => 
      t.id === task.id 
        ? { ...t, currentStep: 'Starting development server...' }
        : t
    ));
    
    const serverProcess = await webContainerIntegration.startDevServer();
    if (serverProcess) {
      setProgress(100);
    } else {
      throw new Error('Failed to start development server');
    }
  };

  const executeDefaultTask = async (task) => {
    // Fallback for unknown tasks
    setProgress(50);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setProgress(100);
  };
  
  // Start development server
  const startDevServer = useCallback(async () => {
    if (!webContainer) return;
    
    try {
      const installProcess = await webContainer.spawn('npm', ['install']);
      await installProcess.exit;
      
      const serverProcess = await webContainer.spawn('npm', ['run', 'dev']);
      
      // Listen for server ready
      serverProcess.output.pipeTo(new WritableStream({
        write(data) {
          const text = new TextDecoder().decode(data);
          if (text.includes('localhost')) {
            const match = text.match(/localhost:(\d+)/);
            if (match) {
              setPreviewUrl(`http://localhost:${match[1]}`);
            }
          }
        }
      }));
      
    } catch (error) {
      console.error('Failed to start dev server:', error);
    }
  }, [webContainer]);
  
  // Render file tree
  const renderFileTree = (tree, path = '') => {
    return Object.entries(tree).map(([name, node]) => {
      const fullPath = path ? `${path}/${name}` : name;
      
      if (node.type === 'folder') {
        const isExpanded = expandedFolders.has(fullPath);
        
        return (
          <div key={fullPath}>
            <ListItem
              button
              onClick={() => {
                setExpandedFolders(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(fullPath)) {
                    newSet.delete(fullPath);
                  } else {
                    newSet.add(fullPath);
                  }
                  return newSet;
                });
              }}
              sx={{ pl: 2 + (fullPath.split('/').length - 1) * 2 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isExpanded ? <FolderOpen /> : <Folder />}
              </ListItemIcon>
              <ListItemText primary={name} />
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={isExpanded}>
              {renderFileTree(node.children, fullPath)}
            </Collapse>
          </div>
        );
      } else {
        const isCreating = creatingFiles.has(fullPath);
        const isUpdating = updatingFiles.has(fullPath);
        const isRecentlyCreated = recentlyCreated.has(fullPath);
        
        return (
          <ListItem
            key={fullPath}
            button
            onClick={() => openFile(fullPath)}
            selected={activeFile?.path === fullPath}
            sx={{ 
              pl: 4 + (fullPath.split('/').length - 1) * 2,
              backgroundColor: 
                isCreating ? 'rgba(76, 175, 80, 0.2)' :
                isUpdating ? 'rgba(255, 193, 7, 0.2)' :
                isRecentlyCreated ? 'rgba(76, 175, 80, 0.1)' :
                'inherit',
              borderLeft: 
                isCreating ? '3px solid #4CAF50' :
                isUpdating ? '3px solid #FFC107' :
                isRecentlyCreated ? '2px solid #4CAF50' :
                'none',
              transition: 'all 0.3s ease',
              animation: isCreating ? 'pulse 1.5s infinite' : 'none'
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Box position="relative">
                <InsertDriveFile />
                {isCreating && (
                  <CircularProgress 
                    size={16} 
                    sx={{ 
                      position: 'absolute', 
                      top: -8, 
                      right: -8,
                      color: '#4CAF50'
                    }} 
                  />
                )}
                {isUpdating && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#FFC107',
                      animation: 'pulse 1s infinite'
                    }}
                  />
                )}
                {isRecentlyCreated && (
                  <Chip 
                    label="NEW" 
                    size="small" 
                    color="success"
                    sx={{ 
                      position: 'absolute',
                      top: -12,
                      right: -20,
                      fontSize: '0.6rem',
                      height: 16,
                      transform: 'scale(0.8)'
                    }}
                  />
                )}
              </Box>
            </ListItemIcon>
            <ListItemText 
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <span style={{ 
                    fontWeight: isRecentlyCreated ? 'bold' : 'normal',
                    color: isCreating ? '#4CAF50' : isUpdating ? '#FFC107' : 'inherit'
                  }}>
                    {name}
                  </span>
                  {isCreating && <Typography variant="caption" color="success.main">Creating...</Typography>}
                  {isUpdating && <Typography variant="caption" color="warning.main">Updating...</Typography>}
                </Box>
              }
            />
          </ListItem>
        );
      }
    });
  };
  
  // Render task list
  const renderTaskList = () => (
    <Paper elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box p={2} borderBottom={1} borderColor="divider">
        <Typography variant="h6">Project Tasks</Typography>
      </Box>
      
      <Box flexGrow={1} overflow="auto">
        <List>
          {tasks.map(task => (
            <ListItem key={task.id} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <Box display="flex" alignItems="center" width="100%" mb={1}>
                <ListItemIcon>
                  {task.status === 'completed' ? (
                    <CheckCircle color="success" />
                  ) : task.status === 'running' ? (
                    <CircularProgress size={24} />
                  ) : task.status === 'error' ? (
                    <ErrorIcon color="error" />
                  ) : (
                    <Schedule color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={task.name}
                  secondary={task.currentStep || task.description}
                />
                {task.status === 'pending' && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PlayArrow />}
                    onClick={() => executeTask(task)}
                    disabled={isGenerating}
                  >
                    Run
                  </Button>
                )}
              </Box>
              
              {task.status === 'running' && (
                <Box width="100%">
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption" color="textSecondary">
                    {progress.toFixed(0)}% complete
                  </Typography>
                </Box>
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
  );
  
  // Render editor tabs
  const renderEditorTabs = () => (
    <Box borderBottom={1} borderColor="divider">
      <Tabs
        value={openFiles.findIndex(f => f.path === activeFile?.path)}
        onChange={(e, newValue) => setActiveFile(openFiles[newValue])}
        variant="scrollable"
        scrollButtons="auto"
      >
        {openFiles.map((file, index) => (
          <Tab
            key={file.path}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>{file.name}</span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeFile(file.path);
                  }}
                >
                  ×
                </IconButton>
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
  
  // Main layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Alert severity="info">
          Mobile view - Switch to desktop for full development experience
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ height: '100vh', display: 'grid', gridTemplateColumns: '320px 300px 1fr', overflow: 'hidden' }}>
      {/* Left Panel - Tasks */}
      <Box sx={{ borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
        {renderTaskList()}
      </Box>
      
      {/* File Explorer */}
      <Paper elevation={0} sx={{ borderRadius: 0, borderRight: 1, borderColor: 'divider' }}>
        <Box p={1} borderBottom={1} borderColor="divider" display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">Files</Typography>
          <IconButton size="small" onClick={() => updateFileTree(webContainer)}>
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
        <Box overflow="auto" height="calc(100vh - 48px)">
          <List dense>
            {renderFileTree(fileTree)}
          </List>
        </Box>
      </Paper>
      
      {/* Main Editor + Preview + Terminal Area */}
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr 200px' }}>
        {/* Top Tabs */}
        <Box borderBottom={1} borderColor="divider">
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab icon={<Code />} label="Code" value="editor" />
            <Tab icon={<Preview />} label="Preview" value="preview" />
          </Tabs>
        </Box>
        
        {/* Content Area */}
        <Box overflow="hidden">
          {activeTab === 'editor' ? (
            <Box height="100%" display="flex" flexDirection="column">
              {openFiles.length > 0 && renderEditorTabs()}
              <Box flexGrow={1}>
                {activeFile ? (
                  <MonacoEditor
                    height="100%"
                    language={getLanguageFromPath(activeFile.path)}
                    value={activeFile.content}
                    onChange={(value) => {
                      if (value !== undefined) {
                        setActiveFile(prev => ({ ...prev, content: value }));
                        saveFile(activeFile.path, value);
                      }
                    }}
                    options={{
                      minimap: { enabled: true },
                      fontSize: 14,
                      wordWrap: 'on',
                      automaticLayout: true,
                      theme: 'vs-dark'
                    }}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#1e1e1e">
                    <Typography variant="h6" color="grey.500">
                      Select a file to edit
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box height="100%">
              {previewUrl ? (
                <iframe
                  ref={previewRef}
                  src={previewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Preview"
                />
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    🚀 Ready for Preview
                  </Typography>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    Start the development server to see your application preview here
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={startDevServer}
                    disabled={!webContainer}
                    sx={{ mt: 2 }}
                  >
                    Start Preview
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
        
        {/* Terminal Area */}
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <TerminalComponent webContainer={webContainer} height={200} />
        </Box>
      </Box>
    </Box>
  );
};

// Helper function to get language from file path
const getLanguageFromPath = (path) => {
  const ext = path.split('.').pop()?.toLowerCase();
  const languageMap = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'py': 'python',
    'html': 'html',
    'css': 'css',
    'scss': 'scss',
    'json': 'json',
    'md': 'markdown',
    'txt': 'plaintext'
  };
  return languageMap[ext] || 'plaintext';
};

export default BoltStyleCodeDevelopment;